import { importSurfaceBundle } from './bundle-importer.js';
import { bustBundleUrl, coldStartSleep, loadWithColdStartRetry, probeColdStart } from './cold-start.js';
import { SurfaceRuntimeError } from './errors.js';
/** The deadline on one cold-start probe `GET` of a bundle URL. An aborted probe classifies like a network error. */
export const BUNDLE_PROBE_TIMEOUT_MS = 5_000;
/**
 * Hosts a bundle or stylesheet URL may never name, even on the document's own origin: the cloud
 * metadata endpoints. IP literals are refused separately.
 */
const METADATA_HOSTS = new Set(['metadata.google.internal', 'metadata.goog', 'metadata']);
/** True when a WHATWG-parsed hostname is an IP literal: a bracketed IPv6 address or a dotted IPv4 address. */
function isIpLiteral(hostname) {
    return hostname.startsWith('[') || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}
/**
 * The cache key for one bundle: `<appId>@<version> <bundleUrl>`. R-1 gives each `uiPackage` its own
 * bundle, so two packages of one app version hold two keys; every surface of one package shares
 * one key and one import. Pure.
 */
export function bundleCacheKey(ref) {
    return `${ref.appId}@${ref.version} ${ref.bundleUrl}`;
}
/** The cache key for one surface's picked module: the bundle key, `/`, and the surface name, or `legacy`. Pure. */
export function surfaceCacheKey(ref) {
    return `${bundleCacheKey(ref)}/${ref.declaration === null ? 'legacy' : ref.declaration.name}`;
}
/** `appId@version/surface`, or `appId@version` for a legacy ref: the most an error message or log line names. Pure. */
export function refLabel(ref) {
    const base = `${ref.appId}@${ref.version}`;
    return ref.declaration === null ? base : `${base}/${ref.declaration.name}`;
}
/** The two schemes a document, and so an asset URL, may use (R-44). */
const ASSET_SCHEMES = new Set(['http:', 'https:']);
/**
 * Check one bundle or stylesheet URL before any request leaves the page (00-index.md §3, the asset
 * URL row, read with R-44 and R-53). The allowlist is the document's own origin, in the document's own
 * scheme: production serves `https:` and the dev shell on `http://localhost` serves `http:`.
 * Bundles are served from the data plane's origin under `/_apps/`.
 *
 * Parses `url` with the `URL` constructor against `document.baseURI` and returns the parsed URL
 * when all of these hold, else `null`:
 * - the protocol is `http:` or `https:`;
 * - no username or password is embedded;
 * - the hostname is not an IP literal and not a metadata host;
 * - the parsed origin equals `document.location.origin`, so the scheme, host and port are the
 *   document's. This is `isSameOriginUrl`'s comparison without its two fail-open answers (R-53): a
 *   document whose `location` is `null` (one with no browsing context) or whose origin is not a
 *   string gets `null`, and so does a URL the constructor refuses. The comparison runs on the URL
 *   already resolved against `document.baseURI`, so a `<base>` element pointing elsewhere cannot
 *   move a relative URL off the origin.
 *
 * A document on any other scheme (`file:`, `about:`, `data:`) admits nothing, so two opaque
 * `null` origins never compare equal here. Never throws; a string the constructor refuses gives
 * `null`, and so does a page with no `document`. Pure apart from reading the document's location.
 */
export function checkAssetUrl(url) {
    try {
        const parsed = new URL(url, document.baseURI);
        if (!ASSET_SCHEMES.has(parsed.protocol) || parsed.username !== '' || parsed.password !== '')
            return null;
        if (isIpLiteral(parsed.hostname) || METADATA_HOSTS.has(parsed.hostname))
            return null;
        // lib.dom types `document.location` as never null; a document with no browsing context answers null.
        const documentLocation = document.location;
        const documentOrigin = documentLocation?.origin;
        if (typeof documentOrigin !== 'string')
            return null;
        return parsed.origin === documentOrigin ? parsed : null;
    }
    catch {
        return null;
    }
}
const bundles = new Map();
const surfaceModules = new Map();
/** Drop both caches. `resetSurfaceRuntime` in `./testing` is the only caller. */
export function resetModuleCache() {
    bundles.clear();
    surfaceModules.clear();
}
/** Cache `promise` under `key`, and evict it if it rejects, so a later load tries again. */
function cacheUntilRejected(cache, key, promise) {
    cache.set(key, promise);
    promise.catch(() => {
        if (cache.get(key) === promise)
            cache.delete(key);
    });
    return promise;
}
function probeFetch(url, init) {
    return fetch(url, { ...init, signal: AbortSignal.timeout(BUNDLE_PROBE_TIMEOUT_MS) });
}
/**
 * True when a failed import is a link or parse error: a value whose `name` is `SyntaxError` (#3693).
 * The bundle answered, so a retry fetches the same broken module. A name test and not `instanceof`,
 * because the error can come from another realm. A `name` read that throws answers `false`, so the
 * load keeps the cold-start retry it had before this test existed.
 */
function isLinkError(thrown) {
    try {
        return typeof thrown === 'object' && thrown !== null && Reflect.get(thrown, 'name') === 'SyntaxError';
    }
    catch {
        return false;
    }
}
/**
 * Import one bundle with the cold-start retry, once per {@link bundleCacheKey}. The probe answers
 * `permanent` with no request when the last import failed with a {@link isLinkError}, so a link
 * error imports once; any other failure probes the bundle URL as `probeColdStart` decides. The
 * decision lives here so `cold-start.ts` stays the unchanged copy of `@zion/sdk-react`'s. A failure
 * rejects `load-failed` whose `cause` is the last import error.
 */
function importBundle(ref, href) {
    const key = bundleCacheKey(ref);
    const cached = bundles.get(key);
    if (cached !== undefined)
        return cached;
    let lastFailure;
    const loading = loadWithColdStartRetry(async (attempt) => {
        try {
            return await importSurfaceBundle(bustBundleUrl(href, attempt));
        }
        catch (thrown) {
            lastFailure = thrown;
            throw thrown;
        }
    }, () => (isLinkError(lastFailure) ? Promise.resolve('permanent') : probeColdStart(href, probeFetch)), coldStartSleep()).catch((cause) => {
        throw new SurfaceRuntimeError('load-failed', `${refLabel(ref)} failed to load`, { cause });
    });
    return cacheUntilRejected(bundles, key, loading);
}
function isPlainRecord(value) {
    return typeof value === 'object' && value !== null;
}
/** Wrap a `mount` function found on `owner` as a module, keeping `owner` as its `this`. */
function moduleOf(owner, mount) {
    return { mount: (el, ctx) => Reflect.apply(mount, owner, [el, ctx]) };
}
/**
 * Pick the module for `ref` from a bundle namespace (R-1, WS-2 Q-10):
 * 1. A declared ref and a namespace with an own `surfaces` export: the entry named
 *    `declaration.name` when `surfaces` is an object and that own entry has a `mount` function,
 *    else `no-mount-export`. A map never falls through to the adapter.
 * 2. Otherwise an own `mount` function export makes the namespace the module, for a legacy ref and
 *    for a declared ref whose app declares one surface. With more, one `mount` cannot say which
 *    surface it is: `no-mount-export`.
 * 3. Neither: `host.adaptModule`, where `null` is `no-mount-export`.
 */
function pickModule(ns, ref, host) {
    const missing = new SurfaceRuntimeError('no-mount-export', `${refLabel(ref)} exports no module for this surface`);
    if (ref.declaration !== null && Object.hasOwn(ns, 'surfaces')) {
        const map = ns['surfaces'];
        const entry = isPlainRecord(map) && Object.hasOwn(map, ref.declaration.name) ? map[ref.declaration.name] : undefined;
        if (!isPlainRecord(entry))
            throw missing;
        const mount = entry['mount'];
        if (typeof mount !== 'function')
            throw missing;
        return moduleOf(entry, mount);
    }
    const mount = Object.hasOwn(ns, 'mount') ? ns['mount'] : undefined;
    if (typeof mount === 'function') {
        if (ref.declaration !== null && ref.appSurfaceCount !== 1)
            throw missing;
        return moduleOf(ns, mount);
    }
    const adapted = host.adaptModule(ns, ref);
    if (adapted === null)
        throw missing;
    return adapted;
}
/**
 * Load the module for one resolved ref.
 *
 * Checks `ref.bundleUrl` with {@link checkAssetUrl} (`bad-url` on a miss, before any request),
 * then imports the absolute URL through the installed importer with `loadWithColdStartRetry`,
 * `bustBundleUrl` and `probeColdStart`, as `AppHost.loadBundle` did. The in-flight import is cached
 * under {@link bundleCacheKey}, so every surface of one bundle shares one import, and the picked
 * module under {@link surfaceCacheKey}. A rejected promise is evicted from both caches.
 *
 * Rejects {@link SurfaceRuntimeError}: `bad-url`; `load-failed` for an exhausted or permanent
 * import, with the last import error as its `cause`; `no-mount-export` when the pick finds nothing.
 * An import that fails with a `SyntaxError` (a link or parse error) is permanent: one import, no
 * probe, no retry (#3693). Any other import failure keeps the cold-start retry. A throw from `host.adaptModule`
 * propagates as it was thrown.
 */
export function loadSurfaceModule(ref, host) {
    const key = surfaceCacheKey(ref);
    const cached = surfaceModules.get(key);
    if (cached !== undefined)
        return cached;
    const href = checkAssetUrl(ref.bundleUrl);
    if (href === null) {
        return Promise.reject(new SurfaceRuntimeError('bad-url', `${refLabel(ref)} has a bundle URL the runtime refuses`));
    }
    return cacheUntilRejected(surfaceModules, key, importBundle(ref, href.href).then((ns) => pickModule(ns, ref, host)));
}
//# sourceMappingURL=loader.js.map